import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { array } from 'prop-types';

import { ToastContext } from './context/ToastContext';
import FeaturedProductItem from './FeaturedProductItem';

import '../styles/FeaturedProducts.css';
import { LoadingContext } from './context/LoadingContext';
import { buildProducts } from './ProductList';
import { Spinner } from 'react-bootstrap';


const DEFAULT_FEATURED_PRODUCT_IDS = [];
const DEFAULT_PRODUCT_COUNT = 10;
const SORT_BY_FEATURED_ORDER_KEY = 'FEATURED_ORDER';

function FeaturedProducts({ featuredProductIds = DEFAULT_FEATURED_PRODUCT_IDS }) {
    /** Contexts */
    const { sendMessage } = useContext(ToastContext);
    const { startLoading, finishLoading } = useContext(LoadingContext);

    /** States */
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    /** Memoized Values */
    const productsData = useMemo(() => buildProducts(featuredProducts), [featuredProducts]);

    /** Memoized Functions */
    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            startLoading();
            const url = new URL('/api/products', process.env.APP_URL);
            if (featuredProductIds?.length > 0) {
                url.searchParams.append('productIds', featuredProductIds.join(','));
            } else {
                url.searchParams.append('limit', DEFAULT_PRODUCT_COUNT);
                url.searchParams.append('isFeatured', true);
                url.searchParams.append('sortBy', SORT_BY_FEATURED_ORDER_KEY);
            }
            const response = await fetch(url, {
                headers: { 'Content-Type': 'application/json' },
            });
            const data = await response.json();
            setFeaturedProducts(data?.products ?? []);
        } catch (e) {
            sendMessage(e.message, 'error');
        } finally {
            setLoading(false);
            finishLoading();
        }
    }, [featuredProductIds, finishLoading, sendMessage, startLoading]);

    /** Effects */
    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    if (!featuredProducts?.length && !loading) {
        return null;
    }

    return (
        <div
            className={'featured-products--container'}
        >
            <div
                className={'featured-products--heading'}>
                <hr
                    className={'featured-products--line'} />
                <h3
                    className={'featured-products--text'}>FEATURED PRODUCTS</h3>
                <hr
                    className={'featured-products--line'} />
            </div>
            <div
                className={'featured-products--wrapper'}
            >
                {loading ? <div
                    style={{
                        width: '100%',
                    }}
                    className={'d-flex justify-content-center align-items-center'}>
                    <Spinner></Spinner>
                </div> : null}
                {featuredProducts?.length ? productsData.map(({ title, price, imageUrl, id, handle, totalReviews, availableForSale, inventoryLevelId, availableQuantity }) => (
                    <FeaturedProductItem
                        key={id}
                        title={title}
                        price={price}
                        imageUrl={imageUrl}
                        handle={handle}
                        availableForSale={availableForSale}
                        inventoryLevelId={inventoryLevelId}
                        quantity={availableQuantity}
                        totalReviews={totalReviews}
                    />
                )) : null}
            </div>
        </div>
    );
}

FeaturedProducts.propTypes = {
    featuredProductIds: array,
};

export default FeaturedProducts;